import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Typography,
	Box,
	IconButton,
	Button,
	Theme,
} from "@material-ui/core";
import React from "react";
import { useTheme } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import { Tag, TagType, TagColor, TagColors, getTagColorTheme } from "../../entities/customer/Tag";
import { TagService } from "../../services/customer/TagService";
import { useAlert } from "../../hooks/useAlert";

interface CreateTagDialogProps {
	open: boolean;
	type: TagType;
	onClose: () => void;
	onTagCreated: (tag: Tag) => void;
}

export function CreateTagDialog(props: CreateTagDialogProps) {
	const { open, type, onClose, onTagCreated } = props;
	const theme = useTheme<Theme>();
	const alert = useAlert();

	const [selectedColor, setSelectedColor] = React.useState<TagColor>("blue");
	const selectedTheme = getTagColorTheme(selectedColor, theme.palette.type);
	const [name, setName] = React.useState<string>("");
	const [disabled, setDisabled] = React.useState<boolean>(false);
	const disableSave = name.trim().length === 0 || disabled;

	const colorRows = [TagColors.slice(0, 3), TagColors.slice(3, 6), TagColors.slice(6, 9)];

	const onCreateTag = async () => {
		setDisabled(true);
		const result = await TagService.create({ text: name, tagColor: selectedColor, type });
		setDisabled(false);

		if (result.success && result.data) {
			onTagCreated(result.data);
		} else if (result.validation) {
			alert.validation(result);
		} else {
			alert.error("Failed to create tag");
		}
	};

	return (
		<Dialog open={open} onClose={disabled ? undefined : onClose} maxWidth="xs">
			<DialogTitle>{`Create ${type} Tag`}</DialogTitle>
			<DialogContent>
				<Box py={2}>
					{/* Tag Name Input */}
					<TextField 
						label={`Tag Name *`} 
						value={name} 
						variant="outlined" 
						required 
						disabled={disabled} 
						onChange={(e) => setName(e.target.value)} 
						fullWidth 
					/>
					
					{/* Example Tag Preview */}
					<Box mt={2} mb={3} display="flex" justifyContent="center">
						<div 
							style={{ 
								color: selectedTheme.text, 
								backgroundColor: selectedTheme.background,
								padding: "6px 12px",
								borderRadius: 16,
								display: "inline-block"
							}}
						>
							{name === "" ? "Example Tag" : name}
						</div>
					</Box>

					{/* Color Selection */}
					<Typography variant="body2">Choose a color</Typography>
					<Box mt={1}>
						{colorRows.map((colorRow, index) => (
							<Box key={index} display="flex" justifyContent="center">
								{colorRow.map((color) => (
									<IconButton
										key={color}
										size="small"
										disabled={disabled}
										onClick={() => setSelectedColor(color)}
										style={{
											backgroundColor: getTagColorTheme(color, theme.palette.type).background,
											color: getTagColorTheme(color, theme.palette.type).text,
											borderRadius: 0,
											width: 40,
											height: 40,
											margin: 2
										}}
									>
										{selectedColor === color ? <CheckIcon /> : null}
									</IconButton>
								))}
							</Box>
						))}
					</Box>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" disabled={disabled} onClick={onClose}>
					Cancel
				</Button>
				<Button variant="contained" color="primary" disabled={disableSave} onClick={onCreateTag}>
					Create
				</Button>
			</DialogActions>
		</Dialog>
	);
}
